import { useContext, useMemo } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading } from "@stubhub/broadway-web";
import type { EventGridItem, MappingPagedCollectionOfEventSearchResultAndEventGridItem } from "src/http/types";
import { useContent, useFormattedContent } from "src/shared/components/content";
import type { ContentId } from "src/shared/components/content/types";
import { LocationContext } from "src/shared/context/location-context";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useSyncedQuery } from "src/shared/hooks/useSyncedQuery";
import { useIsMobileClientApp } from "src/shared/utils/appContextHelper";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { STALE_TIME_ONE_HOUR } from "src/shared/utils/query/retry-constants";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { GridCardBase } from "src/website-apps/shared/components/grid-card-base";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { ImageSize } from "src/website-apps/shared/components/image";
import { CAROUSEL_SIZES, HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";
import { DateContext } from "src/website-apps/shared/context/date-context";
import { useCarouselDimensions } from "src/website-apps/shared/hooks/useCarouselDimensions";
import { styles } from "../marquee-hero-carousel/marquee-hero-carousel.recipes";

// "More from Genres You Like" — exp 102911 (discovery-web-mb-music-you-like, marquee treatment)
// and its relaunch exp 103106 (discovery-web-mb-music-you-like-v2, standard-carousel treatment,
// variant-driven title).
// Events come from the lazy MoreFromGenresYouLike endpoint, which resolves the user's
// MusicBrainz genre affinity from recently viewed events + categories.
const TITLE_CONTENT_ID: ContentId = "cid$#4260880#"; // "More from Genres You Like"
const BECAUSE_YOU_LIKE_CONTENT_ID: ContentId = "cid$#4282899#"; // "Because you like {0}" ([AB 103106])
const FETCH_METHOD = "MoreFromGenresYouLike";

// The lazy MoreFromGenresYouLike endpoint isn't in the OpenAPI spec (the schema is borrowed from
// other typed endpoints), so the collection-level metadata the controller attaches is typed here.
type MoreFromGenresYouLikeResponse = MappingPagedCollectionOfEventSearchResultAndEventGridItem & {
  readonly metadata?: { readonly genreNames?: readonly string[] | null } | null;
};

// Genre names arrive lowercase from the MusicBrainz index (e.g. "indie rock", "r&b") and
// English-only — the backend omits them for non-English site languages, so hardcoded English
// title-casing and joining ("Indie Rock, Pop & Shoegaze") is safe here.
const formatGenreList = (genreNames: readonly string[]): string => {
  const titleCased = genreNames.map((name) => name.replaceAll(/\b\w/g, (char) => char.toUpperCase()));
  if (titleCased.length <= 1) {
    return titleCased[0] ?? "";
  }
  return `${titleCased.slice(0, -1).join(", ")} & ${titleCased.at(-1)}`;
};

interface MoreFromGenresYouLikeQueryOptions {
  /**
   * Narrow retrieval to the user's top N genres so the title can name every genre the
   * events came from (relaunch variant 2 uses 2). Omitted = backend default breadth (12).
   */
  maxGenres?: number | undefined;
  /** Only fetch for users whose experiment arm can actually render the collection. */
  enabled: boolean;
}

/**
 * Fetches the "More from Genres You Like" collection. Lives at the carousel-registry level
 * (not inside the component) so the slot can be excluded from the registry until the data
 * confirms there is something to render — a slot that renders null still consumes a
 * `visibleIndex`/`CarouselProvider`, shifting `entityPosition` for every carousel below it
 * and corrupting cross-arm telemetry comparisons (same reasoning as the Buy It Again slot).
 *
 * Grounded in the user's current location + selected dates: putting lt/lg/from/to in the
 * query key makes React Query refire the fetch whenever the location or dates change — the
 * same mechanism the other location/date-aware home carousels use.
 */
export const useMoreFromGenresYouLikeQuery = ({ maxGenres, enabled }: MoreFromGenresYouLikeQueryOptions) => {
  const createQueryFn = useCreateQueryFn();
  const [userLocation] = useContext(LocationContext);
  const dateContext = useContext(DateContext);
  const { lt, lg } = userLocation ?? {};
  const { from, to } = dateContext?.[0]?.dates ?? {};
  const fromDate = from ? (from instanceof Date ? from.toJSON() : from) : undefined;
  const toDate = to ? (to instanceof Date ? to.toJSON() : to) : undefined;

  const queryKey = useMemo(
    () => createQueryKey(QueryKeyPrefix.EVENT_CAROUSEL, { method: FETCH_METHOD, lt, lg, fromDate, toDate, maxGenres }),
    [lt, lg, fromDate, toDate, maxGenres],
  );

  return useSyncedQuery<MoreFromGenresYouLikeResponse>({
    queryKey,
    queryFn: createQueryFn<MoreFromGenresYouLikeResponse>({
      method: FETCH_METHOD,
      params: [
        ...(lt !== null && lt !== undefined ? [["lt", lt] as [string, number]] : []),
        ...(lg !== null && lg !== undefined ? [["lg", lg] as [string, number]] : []),
        ...(fromDate ? [["fromDate", fromDate] as [string, string]] : []),
        ...(toDate ? [["toDate", toDate] as [string, string]] : []),
        ...(maxGenres === undefined ? [] : [["maxGenres", maxGenres] as [string, number]]),
      ],
    }),
    staleTime: STALE_TIME_ONE_HOUR,
    enabled,
  });
};

interface MoreFromGenresYouLikeProps {
  /**
   * marquee = original 102911 treatment (large marquee GridCardBase cards);
   * standard = relaunch treatment, rendered like the other home event carousels.
   */
  display: "marquee" | "standard";
  /**
   * plain = "More from Genres You Like" (relaunch variant 1);
   * genreSuffix = plain title + top genres appended (102911 marquee);
   * becauseYouLike = "Because you like Indie Rock & Pop" (relaunch variant 2).
   * Genre-bearing modes fall back to plain when genre names are absent (non-English locales).
   */
  titleMode: "plain" | "genreSuffix" | "becauseYouLike";
  /** Collection data from `useMoreFromGenresYouLikeQuery`, fetched at the registry level. */
  data: MoreFromGenresYouLikeResponse | undefined;
}

/**
 * Personalized concert events ranked by the genres the user likes. Renders either the
 * 102911 marquee treatment (mirroring `MarqueeHeroCarousel`) or, for the relaunch
 * experiment (103106), a standard home event carousel. The registry only includes the slot
 * when the data has items, so the empty return here is a safety net, not the primary gate.
 */
export const MoreFromGenresYouLike = ({ display, titleMode, data }: MoreFromGenresYouLikeProps) => {
  const isDesktop = useMediaQuery(Breakpoint.Tablet);
  const header = useContent(TITLE_CONTENT_ID);

  // Standard-display sizing mirrors the other home event carousels (DontMissEvents):
  // responsive dimensions plus the reduced-card-size experiment override.
  const { carouselSize, imageSize, gapPx } = useCarouselDimensions();
  const reduceEventCardSizes = useIsMobileClientApp();
  const standardCarouselSize = reduceEventCardSizes ? CAROUSEL_SIZES.medium4x3 : carouselSize;
  const standardImageSize = reduceEventCardSizes ? ImageSize.Medium4x3 : imageSize;

  // Top genres that produced the collection — surfaced in the title so it's clear why these
  // events are recommended. Empty for non-English site languages (backend gate), in which case
  // the genre-bearing title modes fall back to the plain header.
  const genreList = formatGenreList(data?.metadata?.genreNames ?? []);
  const becauseYouLikeHeader = useFormattedContent(BECAUSE_YOU_LIKE_CONTENT_ID, [genreList]);

  const items = data?.items ?? [];
  if (items.length === 0) {
    return null;
  }

  const headerText =
    titleMode === "becauseYouLike" && genreList
      ? becauseYouLikeHeader
      : `${header}${titleMode === "genreSuffix" && genreList ? `: ${genreList}` : ""}`;

  const title = (
    <Heading as="h2" variant="header">
      {headerText}
    </Heading>
  );

  if (display === "standard") {
    return (
      <div>
        {title}
        <ResponsiveCarousel
          carouselId={HOME_CAROUSEL_IDS.mbMusicYouLike}
          hasHoverEffect={isDesktop}
          hidePaginationButtons={!isDesktop}
          items={items}
          gapPx={gapPx}
          size={standardCarouselSize}
          imageHeightHint={standardImageSize.height}
          analyticsCarouselId={CarouselId.MoreFromGenresYouLike}
          analyticsItemType={CarouselItemType.Event}
          useFlex
          renderItem={(item: EventGridItem) => (
            <GridCardBase
              headingAs="h3"
              url={item.url ?? ""}
              title={item.eventName ?? ""}
              eventDate={item.localEventDate ?? undefined}
              eventTime={item.localEventTime ?? undefined}
              {...(item.venueName ? { venueName: item.venueName } : {})}
              imageUrl={item.imageUrl ?? ""}
              entityId={item.eventId}
              entityType={ENTITY_TYPES.EVENT}
              formattedFromPrice={item.formattedFromPrice ?? undefined}
              event={item}
              size={isDesktop ? "default" : "mobile"}
            />
          )}
        />
      </div>
    );
  }

  return (
    <div>
      {title}
      <div className={styles.container({ size: isDesktop ? "desktop" : "mobile" })}>
        <ResponsiveCarousel
          carouselId={HOME_CAROUSEL_IDS.mbMusicYouLike}
          classes={{ root: styles.scrollContainer({ size: isDesktop ? "desktop" : "mobile" }) }}
          hasHoverEffect={false}
          hidePaginationButtons={!isDesktop}
          items={items}
          gapPx={isDesktop ? 16 : 12}
          size={isDesktop ? CAROUSEL_SIZES.marqueeDesktop : CAROUSEL_SIZES.marqueeMobile}
          analyticsCarouselId={CarouselId.MoreFromGenresYouLike}
          analyticsItemType={CarouselItemType.Event}
          useFlex
          renderItem={(item: EventGridItem) => (
            <GridCardBase
              headingAs="h3"
              url={item.url ?? ""}
              title={item.eventName ?? ""}
              eventDate={item.localEventDate ?? undefined}
              eventTime={item.localEventTime ?? undefined}
              {...(item.venueName ? { venueName: item.venueName } : {})}
              imageUrl={item.imageUrl ?? ""}
              entityId={item.eventId}
              entityType={ENTITY_TYPES.EVENT}
              variant="marquee"
              formattedFromPrice={item.formattedFromPrice ?? undefined}
              event={item}
              size={isDesktop ? "default" : "mobile"}
              fullHeightImage
            />
          )}
        />
      </div>
    </div>
  );
};
